import { useNavigate, Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { useAutoFocus } from "@/hooks/useAutoFocus";

/**
 * Accessibilità — pagina di documentazione (P1-8). Solo spiegazioni: NON modifica
 * la meccanica di gioco. Copre modalità Accessibilità, VoiceOver (iOS) e TalkBack (Android).
 */
export default function Accessibility() {
  const navigate = useNavigate();
  const headingRef = useAutoFocus();

  return (
    <main className="screen" data-testid="accessibility-screen">
      <div className="screen__header">
        <button
          type="button"
          className="back-btn"
          data-testid="accessibility-back-button"
          onClick={() => navigate("/")}
          aria-label="Torna alla schermata principale"
        >
          <ChevronLeft size={16} strokeWidth={2.5} aria-hidden="true" />
          Indietro
        </button>
        <h1 className="screen__title" tabIndex={-1} ref={headingRef}>
          Accessibilità
        </h1>
      </div>

      <p className="doc__p" data-testid="accessibility-intro">
        FOCUSTAP è pensato per essere giocato anche senza guardare lo schermo. Con la modalità
        Accessibilità attiva, l'app legge ad alta voce ogni richiesta e ogni esito con la propria
        voce: <strong>non è necessario usare VoiceOver o TalkBack per sentire le domande</strong>.
      </p>

      <h2 className="section-label">La modalità Accessibilità</h2>
      <ul className="howto">
        <li className="howto__item">
          <span className="howto__desc">L'app pronuncia la richiesta e l'esito in italiano, con la voce integrata.</span>
        </li>
        <li className="howto__item">
          <span className="howto__desc">
            Le prove che richiedono di vedere lo schermo (contare o riconoscere forme mostrate) vengono
            escluse automaticamente.
          </span>
        </li>
        <li className="howto__item">
          <span className="howto__desc">
            Restano le prove risolvibili con udito e memoria: numeri, conteggi annunciati e ciò che è
            stato letto ad alta voce e va ricordato.
          </span>
        </li>
      </ul>

      <h2 className="section-label">Come rispondere</h2>
      <ul className="howto">
        <li className="howto__item">
          <span className="howto__desc">Ascolta la richiesta e tocca lo schermo il numero di volte richiesto.</span>
        </li>
        <li className="howto__item">
          <span className="howto__desc">"NON TOCCARE" significa 0 tocchi.</span>
        </li>
        <li className="howto__item">
          <span className="howto__desc">
            Alcune prove chiedono di ricordare ciò che è stato annunciato (animali, colori, forme,
            numeri) per una domanda successiva.
          </span>
        </li>
        <li className="howto__item">
          <span className="howto__desc">
            Prima di ogni partita c'è un conto alla rovescia di 5 secondi (5 → VIA!): usalo per
            prepararti.
          </span>
        </li>
      </ul>

      <h2 className="section-label">iPhone e iPad — VoiceOver</h2>
      <p className="doc__p">
        FOCUSTAP ha una voce propria, quindi <strong>non devi attivare VoiceOver</strong> per giocare
        o per sentire le domande.
      </p>
      <p className="doc__p">
        Se VoiceOver è attivo, i tocchi funzionano diversamente (esplorazione al tocco e doppio tap per
        attivare): questo interferisce con il conteggio dei tocchi del gioco.
      </p>
      <p className="doc__p">
        <strong>Consiglio:</strong> mantieni attiva la modalità Accessibilità di FOCUSTAP (per sentire
        le domande) e disattiva temporaneamente VoiceOver mentre giochi. Il conto alla rovescia di 5
        secondi ti dà il tempo di farlo.
      </p>
      <p className="doc__p">
        Suggerimento: puoi attivare o disattivare rapidamente VoiceOver con un triplo clic del tasto
        laterale (o del tasto Home), se hai impostato la scorciatoia di accessibilità.
      </p>

      <h2 className="section-label">Android — TalkBack</h2>
      <p className="doc__p">
        Anche su Android, con la modalità Accessibilità attiva è FOCUSTAP a leggere le domande:{" "}
        <strong>non è necessario TalkBack</strong>.
      </p>
      <p className="doc__p">
        Con TalkBack attivo, i tocchi richiedono il doppio tap e l'esplorazione al tocco, che
        interferiscono con il conteggio dei tocchi.
      </p>
      <p className="doc__p">
        <strong>Consiglio:</strong> tieni attiva la modalità Accessibilità di FOCUSTAP e disattiva
        temporaneamente TalkBack mentre giochi, usando i 5 secondi del conto alla rovescia.
      </p>
      <p className="doc__p">
        Suggerimento: su molti dispositivi puoi attivare o disattivare TalkBack tenendo premuti
        entrambi i tasti del volume per alcuni secondi, se la scorciatoia è abilitata.
      </p>

      <h2 className="section-label">Attivare o disattivare</h2>
      <p className="doc__p" data-testid="accessibility-settings-note">
        Apri le{" "}
        <Link to="/settings" className="doc__link" data-testid="accessibility-settings-link">
          Impostazioni
        </Link>{" "}
        e usa l'interruttore "Modalità accessibilità".
      </p>
    </main>
  );
}
